/**
 * sortHelpers — shared natural-sort utilities used across the app.
 *
 * Use these everywhere a list (especially dropdown options) is presented to
 * the user so addresses like "1 East Sussex Lane", "3A Chancery Lane",
 * "14 Caldecott Close", "117 Binjai" sort the way humans expect, instead of
 * lexicographically (which would put "14" before "3A").
 *
 * All helpers are pure and never mutate their inputs.
 */

/**
 * Locale-aware natural comparison: numbers inside strings are compared by
 * numeric value, and comparison is case-insensitive.
 */
export const naturalCompare = (a = '', b = '') =>
  String(a ?? '').localeCompare(String(b ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });

/**
 * Returns a SORTED COPY of `items`, ordered by the label produced by
 * `getLabel`. Falls back to `item.label`, `item.name`, or the item itself
 * stringified.
 *
 * Never mutates the original array.
 */
export const sortByLabel = (
  items = [],
  getLabel = (item) => {
    if (item == null) return '';
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    return item.label ?? item.name ?? String(item);
  }
) => [...items].sort((a, b) => naturalCompare(getLabel(a), getLabel(b)));

/**
 * Alias for use specifically inside dropdown components — semantically
 * conveys intent at the call site.
 */
export const naturalSortOptions = sortByLabel;

/* ─────────────────────────────────────────────────────────────────────────────
   PROJECT COSTING — row-level sort utilities
   ─────────────────────────────────────────────────────────────────────────── */

/**
 * Returns the effective numeric sort order for a costing row.
 * Prefers the explicit `sortOrder` field added to rows during and after
 * the sort-aware refactor. Falls back to the Firestore `createdAt` timestamp
 * for older rows that pre-date the `sortOrder` field.
 */
export function getSortOrder(row) {
  if (!row) return 0;
  const s = row.sortOrder;
  if (s !== undefined && s !== null && Number.isFinite(Number(s))) return Number(s);
  const ca = row.createdAt;
  if (ca?.toMillis) return ca.toMillis();
  if (ca instanceof Date) return ca.getTime();
  if (typeof ca === 'number') return ca;
  return 0;
}

/**
 * Columns whose values should be compared as dates.
 * Add new date-type column keys here as the schema evolves.
 */
export const DATE_SORT_KEYS = new Set(['date']);

/**
 * Columns whose values should be compared as numbers.
 */
export const NUMERIC_SORT_KEYS = new Set([
  'quantity', 'unitPrice', 'invoiceAmount',
  'totalBeforeGST', 'payableGST', 'totalAfterGST',
  'lastPayment', 'balance',
]);

/**
 * Column keys that are not sortable (icon/control columns).
 */
export const NON_SORTABLE_KEYS = new Set(['expand', 'num', 'actions']);

/**
 * Returns the raw comparable value for a given column key on a cost row.
 * `meta` supplies pre-computed values (e.g. amountPaid, balance) that are not
 * stored directly on the row object.
 *
 * @param {object} row
 * @param {string} key  - COST_COLUMNS key
 * @param {{ paid: number, balance: number }} meta
 * @returns {string|number}
 */
export function getCostRowSortValue(row, key, meta = {}) {
  switch (key) {
    case 'totalBeforeGST':  return Number(row.totalCostBeforeGST) || 0;
    case 'totalAfterGST':   return Number(row.totalCostAfterGST)  || 0;
    case 'lastPayment':     return meta.paid  ?? 0;
    case 'balance':         return meta.balance ?? 0;
    case 'paymentStatus':   return row.paymentStatus ?? '';
    // The column renders a quotation name but the row only stores its id, so
    // sorting groups a subcontractor's claims against the same quotation
    // together rather than ordering them alphabetically. Grouping is what the
    // column is actually read for; ad-hoc claims ('') collect at one end.
    case 'subconQuotation': return row.subconQuotationId ?? '';
    default:                return row[key] ?? '';
  }
}

/**
 * Returns a comparator function for an array of cost rows.
 *
 * @param {string} key       - column key to sort by
 * @param {'asc'|'desc'} dir - sort direction
 * @param {function} getMeta - (row) => { paid, balance }  for computed columns
 * @returns {(a: object, b: object) => number}
 */
export function makeCostRowComparator(key, dir, getMeta = () => ({})) {
  const mult = dir === 'asc' ? 1 : -1;

  return (a, b) => {
    const aVal = getCostRowSortValue(a, key, getMeta(a));
    const bVal = getCostRowSortValue(b, key, getMeta(b));

    if (DATE_SORT_KEYS.has(key)) {
      const at = aVal ? new Date(aVal).getTime() : 0;
      const bt = bVal ? new Date(bVal).getTime() : 0;
      // Push empty dates to the end regardless of direction
      if (!aVal && bVal) return 1;
      if (aVal && !bVal) return -1;
      return (at - bt) * mult;
    }

    if (NUMERIC_SORT_KEYS.has(key)) {
      const an = Number.isFinite(+aVal) ? +aVal : 0;
      const bn = Number.isFinite(+bVal) ? +bVal : 0;
      // Push zero/empty to the end for ascending, to the front for descending
      if (an === 0 && bn !== 0) return 1;
      if (an !== 0 && bn === 0) return -1;
      return (an - bn) * mult;
    }

    // Text: case-insensitive natural sort; empty values to end
    const aStr = String(aVal ?? '').toLowerCase();
    const bStr = String(bVal ?? '').toLowerCase();
    if (!aStr && bStr) return 1;
    if (aStr && !bStr) return -1;
    return naturalCompare(aStr, bStr) * mult;
  };
}

export default {
  naturalCompare,
  sortByLabel,
  naturalSortOptions,
  getSortOrder,
  makeCostRowComparator,
  getCostRowSortValue,
};
